import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Badge } from '@mono/ui-components';
import { memo, useMemo } from 'react';
function formatCurrency(value) {
    if (value === 0) return '$0';
    if (value >= 1000000) return `$${(value / 1000000).toFixed(2)}M`;
    if (value >= 1000) return `$${value.toLocaleString(undefined, {
        maximumFractionDigits: 0
    })}`;
    return `$${value.toFixed(2)}`;
}
export const OverviewPanel = /*#__PURE__*/ memo(function OverviewPanel({ pricing, clinPricing, sharedTierConfigs }) {
    const totalValue = useMemo(()=>clinPricing.reduce((sum, c)=>sum + c.grandTotal, 0), [
        clinPricing
    ]);
    const laborClins = clinPricing.filter((c)=>c.laborStructure.type !== 'none');
    const nonLaborClins = clinPricing.filter((c)=>c.laborStructure.type === 'none');
    // Contract type distribution
    const typeCounts = useMemo(()=>{
        const map = new Map();
        for (const clin of clinPricing){
            const ct = clin.metadata.contractType || 'Unknown';
            map.set(ct, (map.get(ct) ?? 0) + 1);
        }
        return [
            ...map.entries()
        ].sort((a, b)=>b[1] - a[1]);
    }, [
        clinPricing
    ]);
    // Period summaries
    const periodSummaries = useMemo(()=>{
        const map = new Map();
        for (const clin of clinPricing){
            const period = clin.metadata.periodName || 'Unassigned';
            const existing = map.get(period) ?? {
                count: 0,
                total: 0
            };
            existing.count++;
            existing.total += clin.grandTotal;
            map.set(period, existing);
        }
        return [
            ...map.entries()
        ];
    }, [
        clinPricing
    ]);
    // CLINs with warnings (zero totals)
    const warningClins = useMemo(()=>clinPricing.filter((c)=>c.laborStructure.type !== 'none' && c.grandTotal === 0), [
        clinPricing
    ]);
    return /*#__PURE__*/ _jsxs("div", {
        className: "space-y-6",
        children: [
            /*#__PURE__*/ _jsxs("div", {
                className: "grid grid-cols-2 lg:grid-cols-4 gap-4",
                children: [
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Total Value"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: formatCurrency(totalValue)
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "CLINs"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: clinPricing.length
                            }),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-gray-400 mt-0.5",
                                children: [
                                    laborClins.length,
                                    " labor, ",
                                    nonLaborClins.length,
                                    " non-labor"
                                ]
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Labor Categories"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: pricing.rateCard?.categories.length ?? 0
                            })
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "bg-white border border-gray-200 rounded-lg p-4",
                        children: [
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-sm text-gray-500",
                                children: "Shared Tiers"
                            }),
                            /*#__PURE__*/ _jsx("div", {
                                className: "text-2xl font-semibold mt-1",
                                children: sharedTierConfigs.length
                            }),
                            pricing.templateConfidence != null && /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-gray-400 mt-0.5",
                                children: [
                                    (pricing.templateConfidence * 100).toFixed(0),
                                    "% confidence"
                                ]
                            })
                        ]
                    })
                ]
            }),
            periodSummaries.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-semibold text-gray-700 mb-3",
                        children: "Period Summary"
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-2",
                        children: [
                            periodSummaries.map(([period, { count, total }])=>/*#__PURE__*/ _jsxs("div", {
                                    className: "flex items-center justify-between text-sm",
                                    children: [
                                        /*#__PURE__*/ _jsxs("div", {
                                            className: "flex items-center gap-2",
                                            children: [
                                                /*#__PURE__*/ _jsx("span", {
                                                    className: "text-gray-700 font-medium",
                                                    children: period
                                                }),
                                                /*#__PURE__*/ _jsxs("span", {
                                                    className: "text-xs text-gray-400",
                                                    children: [
                                                        count,
                                                        " CLINs"
                                                    ]
                                                })
                                            ]
                                        }),
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "font-medium text-gray-900 tabular-nums",
                                            children: formatCurrency(total)
                                        })
                                    ]
                                }, period)),
                            /*#__PURE__*/ _jsxs("div", {
                                className: "flex items-center justify-between text-sm pt-2 border-t border-gray-200",
                                children: [
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-semibold text-gray-700",
                                        children: "Grand Total"
                                    }),
                                    /*#__PURE__*/ _jsx("span", {
                                        className: "font-semibold text-gray-900 tabular-nums",
                                        children: formatCurrency(totalValue)
                                    })
                                ]
                            })
                        ]
                    })
                ]
            }),
            typeCounts.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "bg-white border border-gray-200 rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsx("h3", {
                        className: "text-sm font-semibold text-gray-700 mb-3",
                        children: "Contract Types"
                    }),
                    /*#__PURE__*/ _jsx("div", {
                        className: "flex flex-wrap gap-2",
                        children: typeCounts.map(([type, count])=>/*#__PURE__*/ _jsxs(Badge, {
                                variant: "outline",
                                className: "text-xs",
                                children: [
                                    type,
                                    ": ",
                                    count
                                ]
                            }, type))
                    })
                ]
            }),
            warningClins.length > 0 && /*#__PURE__*/ _jsxs("div", {
                className: "bg-amber-50 border border-amber-200 rounded-lg p-4",
                children: [
                    /*#__PURE__*/ _jsxs("h3", {
                        className: "text-sm font-semibold text-amber-800 mb-2",
                        children: [
                            "CLINs Needing Attention (",
                            warningClins.length,
                            ")"
                        ]
                    }),
                    /*#__PURE__*/ _jsxs("div", {
                        className: "space-y-1",
                        children: [
                            warningClins.slice(0, 10).map((clin)=>/*#__PURE__*/ _jsxs("div", {
                                    className: "text-xs text-amber-700",
                                    children: [
                                        "CLIN ",
                                        clin.clinNumber,
                                        clin.metadata.title && ` — ${clin.metadata.title}`,
                                        /*#__PURE__*/ _jsx("span", {
                                            className: "text-amber-500 ml-1",
                                            children: "(no pricing data)"
                                        })
                                    ]
                                }, clin.clinNumber)),
                            warningClins.length > 10 && /*#__PURE__*/ _jsxs("div", {
                                className: "text-xs text-amber-500",
                                children: [
                                    "+",
                                    warningClins.length - 10,
                                    " more"
                                ]
                            })
                        ]
                    })
                ]
            })
        ]
    });
});
